/**
 * Go to where a figure comes from, and back (taxregime, founder 18 Oct 2026: "like in excel we have go to the
 * link where it comes from and then go back").
 *
 * A screen that sends a person to another place calls `setTraceBack` first with the address to come back to
 * (period and tab in it) and how far down they had scrolled; the shell then shows one floating Back button on
 * every page until it is pressed or closed. Most callers never call it by hand: `goWithBack` / `<BackLink>`
 * (goback, founder 21 Oct 2026: every link to another place shows it) do it for them.
 *
 * The places are a short stack, newest on top: Tax Comp -> a ledger -> one invoice puts "Back to the ledger" on
 * the invoice and "Back to Tax Comp" on the ledger, one step per press. Kept for the browser tab only
 * (sessionStorage): a trace is a moment's work, never something to find again tomorrow. Tiny on purpose: the
 * shell imports this, the button itself is a lazy chunk.
 */
export interface TraceBack {
    /** where Back returns to: path, search and hash */
    href: string;
    /** what the button says: "Back to Tax Comp" */
    label: string;
    /** the main content's scroll position to put back */
    scroll: number;
    /** who set it, so its own screen can clear it without touching someone else's (`filingNeedsReturn.ts`) */
    id?: string;
    /** when it was set (ms), so one leaving remembered twice is kept once */
    at?: number;
}

const KEY = 'karr.traceBack';
const CHANGED = 'karr:traceback';
/** Deeper than five jumps nobody walks back one by one; the oldest drops off. */
export const TRACE_DEPTH = 5;

function load(): TraceBack[] {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return [];
        const v = JSON.parse(raw) as unknown;
        // a tab opened before goback held one object, not a list
        const list = Array.isArray(v) ? v : [v];
        return list.filter((t): t is TraceBack => typeof (t as TraceBack)?.href === 'string' && (t as TraceBack).href.startsWith('/'));
    } catch {
        return [];
    }
}

function save(list: TraceBack[]): void {
    try {
        if (list.length) sessionStorage.setItem(KEY, JSON.stringify(list));
        else sessionStorage.removeItem(KEY);
    } catch { /* private window: no Back button, the link still works */ }
    window.dispatchEvent(new Event(CHANGED));
}

/**
 * Remember the way back to here. The same place twice in a row is one entry, the newer scroll kept. The same
 * page remembered twice within a second is one leaving told twice (Tax Comp names its own Back, then the
 * router's `useGoWithBack` remembers the page again): the first, more exact one stands.
 */
export function setTraceBack(t: Omit<TraceBack, 'scroll' | 'at'>): void {
    const scroll = document.getElementById('main-content')?.scrollTop ?? window.scrollY ?? 0;
    const at = Date.now();
    const list = load();
    const top = list[list.length - 1];
    if (top && top.at && at - top.at < 1000 && new URL(top.href, 'https://k.local').pathname === new URL(t.href, 'https://k.local').pathname) {
        window.dispatchEvent(new Event(CHANGED));
        return;
    }
    if (top && samePlace(top.href, t.href)) list.pop();
    list.push({ ...t, scroll, at });
    save(list.slice(-TRACE_DEPTH));
}

/** The place the Back button returns to now (the newest), or null. */
export function readTraceBack(): TraceBack | null {
    const list = load();
    return list[list.length - 1] ?? null;
}

/** Back was pressed: the newest place is spent, the one before it (if any) shows next. */
export function popTraceBack(): TraceBack | null {
    const list = load();
    const t = list.pop() ?? null;
    save(list);
    return t;
}

/** Close the Back button (every place), or with an id only the places that screen set. */
export function clearTraceBack(id?: string): void {
    save(id ? load().filter((t) => t.id !== id) : []);
}

export const TRACE_CHANGED = CHANGED;

/**
 * Whether the floating Back shows on the page at `href` (vsidebar-a names the rule). It shows on
 * every page after a person left one by a link (a vertical's Books included) until it is pressed
 * or closed, and never on the very place it would return to. One pill for every way out.
 */
export function traceBackShows(href: string): boolean {
    const t = readTraceBack();
    return !!t && !samePlace(t.href, href);
}

/** Two addresses are the same place when path and search match, whatever order the search keys came in. */
export function samePlace(a: string, b: string): boolean {
    const u = new URL(a, 'https://k.local'), v = new URL(b, 'https://k.local');
    if (u.pathname !== v.pathname) return false;
    u.searchParams.sort(); v.searchParams.sort();
    return u.searchParams.toString() === v.searchParams.toString();
}
